import { ColumnDef } from "@tanstack/react-table";
import React, {
  Dispatch,
  SetStateAction,
  useCallback,
  useMemo,
  useState,
} from "react";
import { Button } from "../../button";
import { ArrowUpDown, Trash } from "lucide-react";
import { t } from "i18next";
import TableWrapper from "../TableWrapper";
import DeleteLocationModal from "../../modal/DeleteLocationModal";

interface Location {
  n_LocationID: number;
  s_LocationName_En: string;
  s_LocationName_Ar: string;
  s_Description: string;
  s_LocationLink: string | null;
}

interface LocationsTableProps {
  locations: Location[];
  setSelectedLocationId: Dispatch<SetStateAction<number>>;
  page: number;
  pages: number;
  setPage: Dispatch<SetStateAction<number>>;
  search: string;
  setSearch: Dispatch<SetStateAction<string>>;
  count: number;
  pageSize: number;
  setPageSize: Dispatch<SetStateAction<number>>;
}

const LocationsTable = ({
  locations,
  setSelectedLocationId,
  pages,
  page,
  setPage,
  search,
  setSearch,
  count,
  pageSize,
  setPageSize,
}: LocationsTableProps) => {
  const [isDeleteLocationModalOpen, setIsDeleteLocationModalOpen] =
    useState(false);
  const [selectedDeleteLocationId, setSelectedDeleteLocationId] = useState(0);

  const handleDeleteLocation = useCallback(async (locationId: number) => {
    setIsDeleteLocationModalOpen(true);
    setSelectedDeleteLocationId(locationId);
  }, []);

  const locationsColumns: ColumnDef<Location>[] = useMemo(
    () => [
      {
        accessorKey: "n_LocationID",
        header: ({ column }) => (
          <Button
            className="flex w-full justify-between text-md"
            type="button"
            variant="ghost"
            onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          >
            {t("locationId")}
            <ArrowUpDown className="ml-2 h-4 w-4" />
          </Button>
        ),
        cell: ({ row }) => {
          return (
            <span
              onClick={() => setSelectedLocationId(row.original.n_LocationID)}
              className="text-blue-800 underline cursor-pointer capitalize"
            >
              {row.original.n_LocationID}
            </span>
          );
        },
      },
      {
        accessorKey: "s_LocationName_En",
        header: ({ column }) => (
          <Button
            className="flex w-full justify-between text-md"
            type="button"
            variant="ghost"
            onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          >
            {t("locationNameEn")}
            <ArrowUpDown className="ml-2 h-4 w-4" />
          </Button>
        ),
        cell: ({ row }) => {
          return <span>{row.original.s_LocationName_En}</span>;
        },
      },
      {
        accessorKey: "s_LocationName_Ar",
        header: ({ column }) => (
          <Button
            className="flex w-full justify-between text-md"
            type="button"
            variant="ghost"
            onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          >
            {t("locationNameAr")}
            <ArrowUpDown className="ml-2 h-4 w-4" />
          </Button>
        ),
        cell: ({ row }) => {
          return <span>{row.original.s_LocationName_Ar}</span>;
        },
      },
      {
        accessorKey: "s_Description",
        header: ({ column }) => (
          <Button
            className="flex w-full justify-between text-md"
            type="button"
            variant="ghost"
            onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          >
            {t("description")}
            <ArrowUpDown className="ml-2 h-4 w-4" />
          </Button>
        ),
        cell: ({ row }) => {
          return <span>{row.original.s_Description}</span>;
        },
      },
      {
        header: t("action"),
        cell: ({ row }) => {
          return (
            <Trash
              onClick={() => handleDeleteLocation(row.original.n_LocationID)}
              className="w-5 h-5 text-red-600 cursor-pointer"
            />
          );
        },
      },
    ],
    [handleDeleteLocation, setSelectedLocationId]
  );

  return (
    <>
      <TableWrapper
        data={locations}
        columns={locationsColumns}
        isColumnChooser={false}
        search={search}
        setSearch={setSearch}
        page={page}
        setPage={setPage}
        totalPages={pages}
        count={count}
        isSearch
        pageSize={pageSize}
        setPageSize={setPageSize}
      />

      <DeleteLocationModal
        locationId={selectedDeleteLocationId}
        isOpen={isDeleteLocationModalOpen}
        onOpenChange={() => setIsDeleteLocationModalOpen(false)}
      />
    </>
  );
};

export default React.memo(LocationsTable);
